/**
 * Mobilization & Demobilization Calculator Component
 * Two separate calculators for Mobilization and Demobilization tasks
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Clock, Users, HardHat, Plus, Trash2, Fuel, Package, DollarSign, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

// Single calculator component for either Mobilization or Demobilization
const SingleMobDemobCalculator = ({
  title,
  icon: Icon,
  iconColor,
  bgColor,
  borderColor,
  data,
  setData,
  testIdPrefix
}) => {
  // Equipment form state
  const [equipName, setEquipName] = useState('');
  const [equipQty, setEquipQty] = useState('1');
  const [equipDuration, setEquipDuration] = useState('');
  const [equipDurationType, setEquipDurationType] = useState('days');
  const [equipRate, setEquipRate] = useState('');
  const [equipFuelGalHr, setEquipFuelGalHr] = useState('');
  const [equipFuelCost, setEquipFuelCost] = useState('');
  
  // Labor form state
  const [laborName, setLaborName] = useState('');
  const [laborWorkers, setLaborWorkers] = useState('1');
  const [laborHours, setLaborHours] = useState('8');
  const [laborDuration, setLaborDuration] = useState('');
  const [laborDurationType, setLaborDurationType] = useState('days');
  const [laborRate, setLaborRate] = useState('');
  const [laborOTHours, setLaborOTHours] = useState('');
  const [laborOTRate, setLaborOTRate] = useState('');
  
  // Other items form state
  const [otherDesc, setOtherDesc] = useState('');
  const [otherCost, setOtherCost] = useState('');

  // Section expansion state
  const [expandedSection, setExpandedSection] = useState('equipment');

  const generateId = useCallback(() => {
    return `${testIdPrefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, [testIdPrefix]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Calculate equipment cost with fuel
  const calculateEquipmentCost = (qty, dur, durType, rate, fuelGalHr, fuelCostPerGal) => {
    const q = parseFloat(qty) || 0;
    const d = parseFloat(dur) || 0;
    const r = parseFloat(rate) || 0;
    const fg = parseFloat(fuelGalHr) || 0;
    const fc = parseFloat(fuelCostPerGal) || 0;
    
    let totalDays = 0;
    let totalHours = 0;
    switch (durType) {
      case 'hours': totalDays = d / 8; totalHours = d; break;
      case 'days': totalDays = d; totalHours = d * 8; break;
      case 'weeks': totalDays = d * 5; totalHours = d * 5 * 8; break;
      default: totalDays = d; totalHours = d * 8;
    }
    
    const equipmentCost = q * totalDays * r;
    const fuelTotalCost = q * totalHours * fg * fc;
    return { equipmentCost, fuelTotalCost, totalCost: equipmentCost + fuelTotalCost };
  };

  // Calculate labor cost
  const calculateLaborCost = (workers, hours, dur, durType, rate, otHours, otRate) => {
    const w = parseFloat(workers) || 0;
    const h = parseFloat(hours) || 8;
    const d = parseFloat(dur) || 0;
    const r = parseFloat(rate) || 0;
    const ot = parseFloat(otHours) || 0;
    const otr = parseFloat(otRate) || 0;
    
    let totalDays = 0;
    switch (durType) {
      case 'hours': totalDays = d / h; break;
      case 'days': totalDays = d; break;
      case 'weeks': totalDays = d * 5; break;
      default: totalDays = d;
    }
    
    const regularCost = w * h * totalDays * r;
    const overtimeCost = w * ot * totalDays * otr;
    return { regularCost, overtimeCost, totalCost: regularCost + overtimeCost };
  };

  // Add equipment item
  const addEquipment = () => {
    if (!equipName) {
      toast.error('Please enter equipment name');
      return;
    }
    if (!equipDuration || !equipRate) {
      toast.error('Please enter duration and rate');
      return;
    }

    const { equipmentCost, fuelTotalCost, totalCost } = calculateEquipmentCost(
      equipQty, equipDuration, equipDurationType, equipRate, equipFuelGalHr, equipFuelCost
    );

    const newItem = {
      id: generateId(),
      type: 'equipment',
      name: equipName,
      quantity: parseInt(equipQty) || 1,
      duration: parseFloat(equipDuration) || 0,
      durationType: equipDurationType,
      rate: parseFloat(equipRate) || 0,
      fuelGalHr: parseFloat(equipFuelGalHr) || 0,
      fuelCostPerGal: parseFloat(equipFuelCost) || 0,
      equipmentCost,
      fuelTotalCost,
      totalCost
    };

    setData(prev => ({
      ...prev,
      equipment: [...(prev.equipment || []), newItem]
    }));
    
    toast.success(`${equipName} added`);
    setEquipName('');
    setEquipQty('1');
    setEquipDuration('');
    setEquipRate('');
    setEquipFuelGalHr('');
    setEquipFuelCost('');
  };

  // Add labor item
  const addLabor = () => {
    if (!laborName) {
      toast.error('Please enter labor type');
      return;
    }
    if (!laborDuration || !laborRate) {
      toast.error('Please enter duration and rate');
      return;
    }

    const { regularCost, overtimeCost, totalCost } = calculateLaborCost(
      laborWorkers, laborHours, laborDuration, laborDurationType, laborRate, laborOTHours, laborOTRate
    );

    const newItem = {
      id: generateId(),
      type: 'labor',
      name: laborName,
      workers: parseInt(laborWorkers) || 1,
      hoursPerDay: parseFloat(laborHours) || 8,
      duration: parseFloat(laborDuration) || 0,
      durationType: laborDurationType,
      rate: parseFloat(laborRate) || 0,
      otHours: parseFloat(laborOTHours) || 0,
      otRate: parseFloat(laborOTRate) || 0,
      regularCost,
      overtimeCost,
      totalCost
    };

    setData(prev => ({
      ...prev,
      labor: [...(prev.labor || []), newItem]
    }));
    
    toast.success(`${laborName} added`);
    setLaborName('');
    setLaborWorkers('1');
    setLaborHours('8');
    setLaborDuration('');
    setLaborRate('');
    setLaborOTHours('');
    setLaborOTRate('');
  };

  // Add other item
  const addOtherItem = () => {
    if (!otherDesc) {
      toast.error('Please enter description');
      return;
    }
    if (!otherCost) {
      toast.error('Please enter cost');
      return;
    }

    const newItem = {
      id: generateId(),
      type: 'other',
      description: otherDesc,
      cost: parseFloat(otherCost) || 0
    };

    setData(prev => ({
      ...prev,
      other: [...(prev.other || []), newItem]
    }));
    
    toast.success('Item added');
    setOtherDesc('');
    setOtherCost('');
  };

  // Remove item
  const removeItem = (itemType, itemId) => {
    setData(prev => ({
      ...prev,
      [itemType]: (prev[itemType] || []).filter(item => item.id !== itemId)
    }));
    toast.info('Item removed');
  };

  // Calculate totals
  const equipmentTotal = (data?.equipment || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const laborTotal = (data?.labor || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const otherTotal = (data?.other || []).reduce((sum, item) => sum + (item.cost || 0), 0);
  const grandTotal = equipmentTotal + laborTotal + otherTotal;

  return (
    <Card className={`border-l-4 ${borderColor}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            {title}
          </CardTitle>
          <Badge className={`${bgColor} text-base`}>
            {formatCurrency(grandTotal)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className={`p-3 rounded-lg ${bgColor} border ${borderColor}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">Equipment</span>
              <HardHat className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-lg font-bold">{formatCurrency(equipmentTotal)}</p>
            <p className="text-xs text-muted-foreground">{(data?.equipment || []).length} items</p>
          </div>
          <div className={`p-3 rounded-lg ${bgColor} border ${borderColor}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">Labor</span>
              <Users className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-lg font-bold">{formatCurrency(laborTotal)}</p>
            <p className="text-xs text-muted-foreground">{(data?.labor || []).length} items</p>
          </div>
          <div className={`p-3 rounded-lg ${bgColor} border ${borderColor}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-muted-foreground">Other</span>
              <Package className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-lg font-bold">{formatCurrency(otherTotal)}</p>
            <p className="text-xs text-muted-foreground">{(data?.other || []).length} items</p>
          </div>
        </div>

        {/* Duration */}
        <div className="flex items-center gap-4 flex-wrap p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${iconColor}`} />
            <Label className="text-sm font-medium">{title} Duration</Label>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="0"
              value={data?.duration?.value || ''}
              onChange={(e) => setData(prev => ({
                ...prev,
                duration: { value: e.target.value, unit: prev?.duration?.unit || 'days' }
              }))}
              min="0"
              step="0.5"
              className="w-20 h-8"
              data-testid={`${testIdPrefix}-duration`}
            />
            <Select 
              value={data?.duration?.unit || 'days'} 
              onValueChange={(val) => setData(prev => ({
                ...prev,
                duration: { value: prev?.duration?.value || '', unit: val }
              }))}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
                <SelectItem value="weeks">Weeks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Equipment Section */}
        <Collapsible open={expandedSection === 'equipment'} onOpenChange={() => setExpandedSection(expandedSection === 'equipment' ? '' : 'equipment')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
              <span className="font-medium flex items-center gap-2">
                <HardHat className="w-4 h-4" />
                Equipment ({(data?.equipment || []).length})
              </span>
              {expandedSection === 'equipment' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Equipment Name</Label>
                <Input
                  placeholder="e.g., Lowboy Trailer"
                  value={equipName}
                  onChange={(e) => setEquipName(e.target.value)}
                  className="h-8"
                  data-testid={`${testIdPrefix}-equip-name`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Qty</Label>
                <Input type="number" min="1" value={equipQty} onChange={(e) => setEquipQty(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rate ($/day)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={equipRate} onChange={(e) => setEquipRate(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Duration</Label>
                <Input type="number" min="0" step="0.5" placeholder="0" value={equipDuration} onChange={(e) => setEquipDuration(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Select value={equipDurationType} onValueChange={setEquipDurationType}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="weeks">Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fuel (gal/hr)</Label>
                <Input type="number" min="0" step="0.1" placeholder="0" value={equipFuelGalHr} onChange={(e) => setEquipFuelGalHr(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fuel ($/gal)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={equipFuelCost} onChange={(e) => setEquipFuelCost(e.target.value)} className="h-8" />
              </div>
            </div>
            <Button onClick={addEquipment} size="sm" className="w-full" data-testid={`${testIdPrefix}-add-equipment`}>
              <Plus className="w-4 h-4 mr-2" /> Add Equipment
            </Button>
            {(data?.equipment || []).map(item => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                <div>
                  <span className="font-medium">{item.name}</span>
                  <span className="text-muted-foreground ml-2">
                    {item.quantity} × {item.duration} {item.durationType} @ ${item.rate}/day
                    {item.fuelTotalCost > 0 && <Badge variant="outline" className="ml-1 text-xs">+Fuel</Badge>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(item.totalCost)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeItem('equipment', item.id)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>

        {/* Labor Section */}
        <Collapsible open={expandedSection === 'labor'} onOpenChange={() => setExpandedSection(expandedSection === 'labor' ? '' : 'labor')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
              <span className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Labor ({(data?.labor || []).length})
              </span>
              {expandedSection === 'labor' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Labor Type</Label>
                <Input placeholder="e.g., Transport Crew" value={laborName} onChange={(e) => setLaborName(e.target.value)} className="h-8" data-testid={`${testIdPrefix}-labor-name`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Workers</Label>
                <Input type="number" min="1" value={laborWorkers} onChange={(e) => setLaborWorkers(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hrs/Day</Label>
                <Input type="number" min="1" max="24" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Duration</Label>
                <Input type="number" min="0" step="0.5" placeholder="0" value={laborDuration} onChange={(e) => setLaborDuration(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Select value={laborDurationType} onValueChange={setLaborDurationType}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="weeks">Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rate ($/hr)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={laborRate} onChange={(e) => setLaborRate(e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">OT Rate ($/hr)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={laborOTRate} onChange={(e) => setLaborOTRate(e.target.value)} className="h-8" />
              </div>
            </div>
            <Button onClick={addLabor} size="sm" className="w-full" data-testid={`${testIdPrefix}-add-labor`}>
              <Plus className="w-4 h-4 mr-2" /> Add Labor
            </Button>
            {(data?.labor || []).map(item => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                <div>
                  <span className="font-medium">{item.name}</span>
                  <span className="text-muted-foreground ml-2">
                    {item.workers} × {item.hoursPerDay}hr × {item.duration} {item.durationType} @ ${item.rate}/hr
                    {item.overtimeCost > 0 && <Badge variant="outline" className="ml-1 text-xs">+OT</Badge>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(item.totalCost)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeItem('labor', item.id)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>

        {/* Other Items Section */}
        <Collapsible open={expandedSection === 'other'} onOpenChange={() => setExpandedSection(expandedSection === 'other' ? '' : 'other')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
              <span className="font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Other Costs ({(data?.other || []).length})
              </span>
              {expandedSection === 'other' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Textarea placeholder="e.g., Permits, Site Security..." value={otherDesc} onChange={(e) => setOtherDesc(e.target.value)} rows={2} data-testid={`${testIdPrefix}-other-desc`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cost ($)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={otherCost} onChange={(e) => setOtherCost(e.target.value)} className="h-8" data-testid={`${testIdPrefix}-other-cost`} />
              </div>
            </div>
            <Button onClick={addOtherItem} size="sm" className="w-full" data-testid={`${testIdPrefix}-add-other`}>
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
            {(data?.other || []).map(item => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                <span className="font-medium">{item.description}</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(item.cost)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeItem('other', item.id)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

const MobilizationCalculator = ({
  mobilizationData,
  setMobilizationData
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Calculate totals for Mobilization
  const mobEquipTotal = (mobilizationData?.mobilization?.equipment || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const mobLaborTotal = (mobilizationData?.mobilization?.labor || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const mobOtherTotal = (mobilizationData?.mobilization?.other || []).reduce((sum, item) => sum + (item.cost || 0), 0);
  const mobilizationTotal = mobEquipTotal + mobLaborTotal + mobOtherTotal;

  // Calculate totals for Demobilization
  const demobEquipTotal = (mobilizationData?.demobilization?.equipment || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const demobLaborTotal = (mobilizationData?.demobilization?.labor || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  const demobOtherTotal = (mobilizationData?.demobilization?.other || []).reduce((sum, item) => sum + (item.cost || 0), 0);
  const demobilizationTotal = demobEquipTotal + demobLaborTotal + demobOtherTotal;

  const grandTotal = mobilizationTotal + demobilizationTotal;

  // Helper to update mobilization data
  const setMobData = (updater) => {
    setMobilizationData(prev => ({
      ...prev,
      mobilization: typeof updater === 'function' ? updater(prev?.mobilization || {}) : updater
    }));
  };

  // Helper to update demobilization data
  const setDemobData = (updater) => {
    setMobilizationData(prev => ({
      ...prev,
      demobilization: typeof updater === 'function' ? updater(prev?.demobilization || {}) : updater
    }));
  };

  return (
    <div className="space-y-6">
      {/* Grand Total Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Mobilization</span>
              <Truck className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(mobilizationTotal)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Demobilization</span>
              <Truck className="w-5 h-5 text-blue-500 scale-x-[-1]" />
            </div>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(demobilizationTotal)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Combined Total</span>
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(grandTotal)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Mobilization Calculator */}
      <SingleMobDemobCalculator
        title="Mobilization"
        icon={Truck}
        iconColor="text-orange-500"
        bgColor="bg-orange-500/5"
        borderColor="border-orange-500/30"
        data={mobilizationData?.mobilization || {}}
        setData={setMobData}
        testIdPrefix="mob"
      />

      {/* Demobilization Calculator */}
      <SingleMobDemobCalculator
        title="Demobilization"
        icon={Truck}
        iconColor="text-blue-500"
        bgColor="bg-blue-500/5"
        borderColor="border-blue-500/30"
        data={mobilizationData?.demobilization || {}}
        setData={setDemobData}
        testIdPrefix="demob"
      />
    </div>
  );
};

export default MobilizationCalculator;
